import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Collapse } from 'react-collapse';
import { CaretLeftNextIcon, ActionableIcon } from '@glu/icons-react';
/** using glu theming because it's a new feature - no point of adding css as well as jss */
import { withStyles } from '@glu/theming';
import classnames from 'classnames';

const styles = ({ screenSizes, spacing, typography }) => ({
  cell: {
    ...typography.p,
    ...typography.p2,
    '&.header': {
      fontSize: 12,
      fontWeight: typography.fontWeights.bold,
    },
    '&:first-child': {
      textAlign: 'left',
    },
    color: '#231f20',
    fontSize: 13,
    lineHeight: '1.2em',
    padding: [spacing.unit * 3, 0],
    textAlign: 'center',
    width: '50%',
    wordBreak: 'break-word',
    [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
      '&:nth-child(2)': {
        textAlign: ({ columns }) => (columns.length < 4 ? 'center' : 'right'),
      },
      width: ({ columns }) => `${100 / columns.length}%`,
    },
  },
  expandCell: {
    '& span': {
      fontSize: 12,
      fontWeight: typography.fontWeights.bold,
      marginRight: 10,
    },
    color: '#231f20',
    fontSize: 13,
  },
  expandWrap: {
    '&.withPadding': {
      paddingBottom: spacing.xs,
    },
    textAlign: 'center',
  },
  mobileToggle: {
    '& svg': {
      fill: '#4c4c4c',
    },
    '&.expanded': {
      transform: 'rotate(90deg)',
    },
    background: 'none',
    display: 'inline-block',
    margin: 0,
    marginRight: spacing.unit,
    padding: 0,
    position: 'relative',
    top: 2,
    transform: 'rotate(0deg)',
    transition: 'rotate, .2s',
  },

  root: {
    background: 'none',
    height: 'inherit',
    margin: [5, 'auto'],
    maxWidth: '100%',
    overflowY: 'auto',
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      '&.grid-desktop': {
        display: 'none',
      },
    },
    [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
      '&.grid-mobile': {
        display: 'none',
      },
    },
  },
  row: {
    '&:last-child': {
      border: 'none',
    },
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      '&.header': {
        borderBottom: '1px solid #ebebeb',
        paddingLeft: 28,
      },
      border: 0,
      padding: [0, spacing.unit * 2],
      paddingLeft: 4, // there is spacing built into the svg
    },
    [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
      minHeight: 33,
    },
    alignItems: 'center',
    borderBottom: '1px solid #ebebeb',
    display: 'flex',
  },
  rowWrap: {
    borderBottom: '1px solid #ebebeb',
  },
});

const Grid = ({ classes, columns, rows }) => {
  const [rowsOpened, setRowsOpened] = useState([]);
  const handleRowToggle = (index) => {
    setRowsOpened((opened) =>
      opened.includes(index) ? opened.filter((e) => e !== index) : opened.concat(index),
    );
  };

  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      <div className={classnames('grid-desktop', classes.root)}>
        <div className={classnames('row', 'header', classes.row)}>
          {columns.map((c) => (
            <div key={`column-${c.name}`} className={classnames('cell', 'header', classes.cell)}>
              {c.label}
            </div>
          ))}
        </div>
        {rows.map((r, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <div key={`row-${i}`} className={classnames('row', classes.row)}>
            {columns.map((c) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <div key={`row-column-${c.name}-${i}`} className={classnames('cell', classes.cell)}>
                {r[c.name] || ' '}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className={classnames('grid-mobile', classes.root)}>
        <div className={classnames('row', 'header', classes.row)}>
          {columns.slice(0, 2).map((c) => (
            <div key={`column-${c.name}`} className={classnames('cell', 'header', classes.cell)}>
              {c.label}
            </div>
          ))}
        </div>
        {rows.map((r, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <div key={`row-${i}`} className={classnames('row-wrap', classes.rowWrap)}>
            <div className={classnames('row', classes.row)}>
              {columns.slice(0, 2).map((c, ic) => (
                /* eslint-disable-next-line react/no-array-index-key */
                <div key={`row-column-${c.name}-${i}`} className={classnames('cell', classes.cell)}>
                  {ic === 0 ? (
                    <ActionableIcon
                      className={classnames(
                        'mobile-toggle',
                        { expanded: rowsOpened.includes(i) },
                        classes.mobileToggle,
                      )}
                      onClick={() => handleRowToggle(i)}
                    >
                      <CaretLeftNextIcon />
                    </ActionableIcon>
                  ) : null}
                  {r[c.name] || ' '}
                </div>
              ))}
            </div>
            <div
              className={classnames(
                'expandWrap',
                { withPadding: rowsOpened.includes(i) },
                classes.expandWrap,
              )}
            >
              <Collapse isOpened={rowsOpened.includes(i)}>
                {columns.slice(2, columns.length).map((c) => (
                  <div
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={`row-column-${c.name}-${i}`}
                    className={classnames('expand-cell', classes.expandCell)}
                  >
                    <span>{c.label}</span>
                    {r[c.name] || ' '}
                  </div>
                ))}
              </Collapse>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

Grid.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  name: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const GridStyled = withStyles(styles)(Grid);

const mapStateToProps = ({ data }, { name }) => {
  const value = JSON.parse(data[name]);
  if (!value || (value && value.rows.length === 0)) {
    return {
      columns: [],
      rows: [],
    };
  }
  return {
    columns: value.columns,
    rows: value.rows,
  };
};

export default connect(mapStateToProps)(GridStyled);
